import { t } from 'i18next';
import { useMutation, useQuery } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';
import { useSelectionCountContext } from 'core/states';
import { GetOneResult } from 'core/types';
import { ProjectType } from 'projects/constants';
import { projectService } from 'projects/services';
import { IProject, IProjectDetails } from 'projects/types';

export const useGetProject = (
  criteria: { id: number },
  enabled = true
): GetOneResult<IProjectDetails> => {
  const { data, isLoading, isError } = useQuery(
    ['project', criteria],
    ({ signal }) => projectService.getProject(criteria, signal),
    { enabled }
  );

  return {
    record: data?.record ?? null,
    isLoading,
    isError,
  };
};

export const useGetDtdProject = (enabled = true): GetOneResult<IProject> => {
  const requestPayload = {
    filters: {
      type: `"${ProjectType.DAY_TO_DAY}"`,
    },
  };

  const { data, isLoading, isFetching, isError } = useQuery(
    ['dtdProject'],
    ({ signal }) => projectService.getManyAutocomplete(requestPayload, signal),
    { enabled }
  );

  return {
    record: data?.records[0] ?? null,
    isLoading,
    isFetching,
    isError,
  };
};

export const useProjectActions = () => {
  const { selectionCount } = useSelectionCountContext();

  return {
    ...useGetDtdProject(),
    ...useUpsertProject(),
    ...useDetachQuotesFromProject(selectionCount),
  };
};

const useUpsertProject = () => {
  const { errors } = useHandleErrors({
    success: ({ project }) =>
      project.id
        ? { message: t('messageProjectUpdateSuccess') }
        : { message: t('messageProjectCreateSuccess') },
    loading: ({ project }) =>
      project.id
        ? { message: t('messageProjectUpdateInProgress') }
        : { message: t('messageProjectCreateInProgress') },
    error: ({ project }) =>
      project.id
        ? { title: t('errorTitleProjectUpdate') }
        : { title: t('errorTitleProjectCreate') },
    invalidateQueries: ['project'],
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation((data) => projectService.upsertProject(data), {
    ...errors,
  });

  return { upsertProject: mutateAsync };
};

const useDetachQuotesFromProject = (selectionCount: number) => {
  const { errors } = useHandleErrors({
    success: {
      message: t('messageDetachVendorQuoteFromProjectSuccess', { count: selectionCount }),
    },
    loading: {
      message: t('messageVendorQuotesDetachFromProjectInProgress', { count: selectionCount }),
    },
    error: { title: t('errorTitleDetachVendorQuoteFromProject', { count: selectionCount }) },
    position: NotificationPosition.DRAWER,
  });

  const { mutateAsync } = useMutation(projectService.removeProjectQuoteAssociation, {
    ...errors,
  });

  return { detachQuotesFromProject: mutateAsync };
};
